import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import NotificationCenter from './NotificationCenter';
import ThemeSwitch from './ThemeSwitch';
import LanguageSelectionDialog from './LanguageSelectionDialog';
import { apiService } from '../services/api';
import './Navigation.css';
import PageHeader from './ui/PageHeader';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());

  // Load unread notification count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const response = await apiService.notifications.getAll({ unreadOnly: true, limit: 1 });
        if (response.data?.data) {
          setUnreadCount(response.data.data.unreadCount);
        }
      } catch (error) {
        console.error('Error loading notification count:', error);
      }
    };

    loadUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    setShowSubmenu(false);
  };

  const closeSubmenu = () => {
    setShowSubmenu(false);
  };

  const mainNavItems = useMemo(() => [
    { path: '/', label: t('navigation:mainNav.dashboard'), icon: '🏠' },
    { path: '/meal-plans', label: t('navigation:mainNav.mealPlans'), icon: '📅' },
    { path: '/shopping-list', label: t('navigation:mainNav.shopping'), icon: '🛒' },
    { path: '/chores', label: t('navigation:mainNav.chores'), icon: '✅' },
  ], [t]);

  const submenuItems = useMemo(() => [
    { path: '/calendar', label: t('navigation:subNav.calendar'), icon: '📆' },
    { path: '/family', label: t('navigation:subNav.family'), icon: '👨‍👩‍👧‍👦' },
    { path: '/recipes', label: t('navigation:subNav.recipes'), icon: '📖' },
  ], [t]);

  const allNavItems = useMemo(() => [...mainNavItems, ...submenuItems], [mainNavItems, submenuItems]);

  // Calculate indicator position for mobile nav
  const updateIndicator = useCallback(() => {
    const activeItem = mainNavItems.find(item => item.path === location.pathname);
    if (activeItem && navContainerRef.current) {
      const activeRef = navItemRefs.current.get(activeItem.path);
      if (activeRef) {
        const containerRect = navContainerRef.current.getBoundingClientRect();
        const itemRect = activeRef.getBoundingClientRect();
        setIndicatorStyle({
          width: `${itemRect.width}px`,
          transform: `translateX(${itemRect.left - containerRect.left}px)`,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle({ opacity: 0 });
    }
  }, [location.pathname, mainNavItems]);

  useEffect(() => {
    updateIndicator();
    // Also update on resize
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  const setNavItemRef = useCallback((path: string) => (el: HTMLAnchorElement | null) => {
    if (el) {
      navItemRefs.current.set(path, el);
    }
  }, []);

  return (
    <>
      <nav className="navigation">
        <PageHeader
          left={(
            <Link to="/" className="brand-link" aria-label="Home">
              <img src="/logo192.png" alt="" className="app-logo" />
            </Link>
          )}
          title={
            location.pathname === '/' ? t('navigation:pageTitles.dashboard')
            : location.pathname.startsWith('/meal-plans') ? t('navigation:pageTitles.mealPlans')
            : location.pathname.startsWith('/shopping-list') ? t('navigation:pageTitles.shopping')
            : location.pathname.startsWith('/chores') ? t('navigation:pageTitles.chores')
            : location.pathname.startsWith('/recipes') ? t('navigation:pageTitles.recipes')
            : location.pathname.startsWith('/family') ? t('navigation:pageTitles.family')
            : location.pathname.startsWith('/calendar') ? t('navigation:pageTitles.calendar')
            : t('navigation:pageTitles.familyPlanner')
          }
          right={(
            <div className="nav-right">
              <ThemeSwitch />
              <div className="nav-notifications">
                <button
                  className="notification-bell"
                  onClick={() => setShowNotifications(true)}
                  aria-label={t('navigation:notifications.title')}
                >
                  🔔
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>
              </div>
              <div className="nav-user">
                <div className="user-info" onClick={() => setShowUserMenu(!showUserMenu)}>
                  <div className="user-avatar">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {user?.name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                </div>
                {showUserMenu && (
                  <div className="user-menu">
                    <div className="menu-item">
                      <span className="menu-label">{t('navigation:userMenu.account')}</span>
                      <span className="menu-value">{user?.email}</span>
                    </div>
                    <div className="menu-divider"></div>
                    <button 
                      className="menu-item language-button" 
                      onClick={() => setShowLanguageDialog(true)}
                    >
                      {t('common:actions.selectLanguage')}
                    </button>
                    <div className="menu-divider"></div>
                    <button className="menu-item logout-button" onClick={handleLogout}>
                      {t('navigation:userMenu.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        />

        {/* Desktop Navigation */}
        <div className="nav-container desktop-nav">
          {allNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Notification Center */}
        <NotificationCenter
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
        />
        
        {/* Language Selection Dialog */}
        <LanguageSelectionDialog
          isOpen={showLanguageDialog}
          onClose={() => setShowLanguageDialog(false)}
        />
      </nav>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <div className="bottom-nav-container" ref={navContainerRef}>
          {/* Animated indicator pill */}
          <div className="bottom-nav-indicator" style={indicatorStyle} />

          {mainNavItems.map((item) => (
            <Link
              key={item.path}
              ref={setNavItemRef(item.path)}
              to={item.path}
              className={`bottom-nav-item ${location.pathname === item.path ? 'active' : ''}`}
              aria-label={item.label}
            >
              <span className="bottom-nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="bottom-nav-label">{item.label}</span>
            </Link>
          ))}

          {/* Submenu Button */}
          <div className="submenu-container">
            <button
              className={`submenu-button ${showSubmenu ? 'active' : ''}`}
              onClick={() => setShowSubmenu(!showSubmenu)}
              aria-label="More options"
            >
              <span className="submenu-icon" aria-hidden="true">⋯</span>
            </button>

            {showSubmenu && (
              <div className="submenu-overlay" onClick={closeSubmenu}>
                <div className="submenu" onClick={(e) => e.stopPropagation()}>
                  {submenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`submenu-item ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={closeSubmenu}
                    >
                      <span className="submenu-item-icon" aria-hidden="true">{item.icon}</span>
                      <span className="submenu-item-label">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation; 